import React from 'react';
import EntityContentType from '@/constants/EntityContentType';
import { PLEDGE_LINK_DONATION_TYPES } from '@/lib/configs/contributions';
import useConstituent from '@/lib/hooks/swr/useConstituent';
import useConstituents from '@/lib/hooks/swr/useConstituents';
import useDonationsSearch from '@/lib/hooks/swr/useDonationsSearch';
import useEntity from '@/lib/hooks/swr/useEntity';
import useEventTickets from '@/lib/hooks/swr/useEventTickets';
import usePreselectedLabels from '@/lib/hooks/swr/usePreselectedLabels';

export const DONATIONS_SEARCH_PARAMS = {
  donationTypes: PLEDGE_LINK_DONATION_TYPES,
  pageFrom: 0,
  // By using pageSize: 10, we can potentially hydrate the data needed for the
  // "Link to Donation" dialog CTA that will be rendered by <DonationContent>.
  // Small performance boost for the user in that one edge case.
  pageSize: 10,
};

const CAMPAIGN_RE = new RegExp(`^${EntityContentType.CAMPAIGN}-`);

export default function useEntityDetailsData({
  constituentId,
  entityId,
  entityType,
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO(ts-migrate)
any) {
  if (!entityType) {
    return {
      error: true,
    };
  }
  const [hasData, setHasData] = React.useState(false);

  // Due to how rendering of different summary details for a campaign works
  // (the UI needs a more specific type then "campaign" but the useEntity
  // fetch can not use those more specific types), we need to reset entity type
  // to "campaign". Else, the useEntity data fetch won't work.
  if (entityType.match(CAMPAIGN_RE)) {
    // eslint-disable-next-line no-param-reassign
    entityType = EntityContentType.CAMPAIGN;
  }

  const isInteraction = entityType === EntityContentType.INTERACTION;
  const { data: constituent } = useConstituent(isInteraction && constituentId);
  const { data: entity, error, mutate } = useEntity(entityId, entityType);

  // @ts-expect-error TS(2339) TODO(ts-migrate): Property 'labelIds' does not exist on type 'obje... Remove this comment to see the full error message
  const labelIds = entity?.labelIds;
  // @ts-expect-error TS(2339) TODO(ts-migrate): Property 'pledgeData' does not exist on type 'obje... Remove this comment to see the full error message
  const pledgeStatusEnum = entity?.pledgeData?.pledgeStatusEnum;
  // @ts-expect-error TS(2339) TODO(ts-migrate): Property 'pledgeDonationId' does not exist on type... Remove this comment to see the full error message
  const pledgeDonationId = entity?.pledgeDonationId;
  // @ts-expect-error TS(2339) TODO(ts-migrate): Property 'softCreditIds' does not exist on type 'o... Remove this comment to see the full error message
  const softCreditIds = entity?.softCreditIds;
  // @ts-expect-error TS(2339) TODO(ts-migrate): Property 'ticketedEventId' does not exist on type 'obje... Remove this comment to see the full error message
  const ticketedEventId = entity?.ticketedEventId;

  const { data: softCredits } = useConstituents(softCreditIds);

  // When dealing with a donation entity, we don't know if we need extra data until
  // the entity is done fetching. Once we have the entity data, we have enough
  // information to know whether or not we need to do additional fetching.
  let needsExtraData = false;

  if (isInteraction) {
    needsExtraData = true;
  }

  const needsLabels = !!labelIds?.tags?.length;
  const needsSoftCredits = softCreditIds?.length && !softCredits;
  const needsTickets = !!ticketedEventId;

  if (!isInteraction && (entity || error)) {
    needsExtraData = !!(
      pledgeStatusEnum ||
      pledgeDonationId ||
      needsLabels ||
      needsSoftCredits ||
      needsTickets
    );
  }

  // If we have a non-pledge donation that is linked a pledge, fetch that pledge
  // data.
  const pledgeDonationResult = useEntity(pledgeDonationId, entityType);
  const pledgeDonation = pledgeDonationResult?.data;

  /**
   * Both labels and tickets can be associated with multiple types of contributions.
   * This makes them optional but required data if the entity has the fields associated
   * with them.
   */
  const { data: labels } = usePreselectedLabels(labelIds?.tags);
  const { data: tickets } = useEventTickets(ticketedEventId);

  const isLabelsRequirementSatisfied = needsLabels ? !!labels : true;
  const isSoftCreditsRequirementSatisfied = needsSoftCredits
    ? !!softCredits
    : true;
  const isTicketsRequirementSatisfied = needsTickets ? !!tickets : true;

  // If we have a pledge donation, fetch donations that are potentially linkable.
  const donationsSearchParams = React.useMemo(
    () => ({
      ...DONATIONS_SEARCH_PARAMS,
      constituentIds: [constituentId],
    }),
    [constituentId],
  );

  const { data: linkableDonations } = useDonationsSearch(
    !!pledgeStatusEnum,
    donationsSearchParams,
  );
  const hasLinkableDonations = !!linkableDonations?.donations;

  React.useEffect(() => {
    if (!(entity || error)) {
      return;
    }

    const areOptionalRequirementsSatisfied =
      isLabelsRequirementSatisfied &&
      isTicketsRequirementSatisfied &&
      isSoftCreditsRequirementSatisfied;

    if (!needsExtraData) {
      setHasData(true);
    } else if (isInteraction && constituent) {
      setHasData(true);
    } else if (
      pledgeStatusEnum &&
      hasLinkableDonations &&
      areOptionalRequirementsSatisfied
    ) {
      setHasData(true);
    } else if (
      pledgeDonationId &&
      pledgeDonation &&
      areOptionalRequirementsSatisfied
    ) {
      setHasData(true);
    } else if (
      !isInteraction &&
      !pledgeStatusEnum &&
      !hasLinkableDonations &&
      !pledgeDonationId &&
      !pledgeDonation &&
      areOptionalRequirementsSatisfied
    ) {
      setHasData(true);
    }
  }, [
    constituent,
    entity,
    error,
    hasLinkableDonations,
    isLabelsRequirementSatisfied,
    isSoftCreditsRequirementSatisfied,
    isTicketsRequirementSatisfied,
    isInteraction,
    needsExtraData,
    pledgeDonation,
    pledgeDonationId,
  ]);

  const resetData = React.useCallback(() => {
    setHasData(false);
  }, []);

  return {
    constituent,
    entity,
    error,
    hasData,
    labels,
    mutate,
    pledgeDonation,
    resetData,
    softCredits,
    tickets,
  };
}
